import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { HomestayCard } from "@/components/HomestayCard";
import { homestayAPI, locationAPI } from "@/lib/api";
import { Search, MapPin, Home, ArrowLeft, Loader2, Filter } from "lucide-react";
import { Button } from "@/components/ui/button";

const SearchResultsPage = () => {
  const { query } = useParams(); // e.g., "homestays-in-dapoli"
  const [homestays, setHomestays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [locationInfo, setLocationInfo] = useState(null);
  
  // Parse the search query to extract location
  const parseQuery = (q) => {
    if (!q) return { location: "", searchType: "all" };
    
    // Pattern: homestays-in-{location}
    const match = q.match(/homestays-in-(.+)/i);
    if (match) {
      return {
        location: match[1].replace(/-/g, " "),
        searchType: "location"
      };
    }
    
    // Default: treat as general search
    return {
      location: q.replace(/-/g, " "),
      searchType: "general"
    };
  };
  
  const { location, searchType } = parseQuery(query);
  const formattedLocation = location.charAt(0).toUpperCase() + location.slice(1);
  
  useEffect(() => {
    const fetchResults = async () => {
      setLoading(true);
      try {
        // Fetch homestays matching the location
        const response = await homestayAPI.getAll({ search: location });
        setHomestays(response.data || []);
        
        // Try to get location info
        try {
          const locResponse = await locationAPI.getAll();
          const matchedLoc = locResponse.data?.find(
            l => l.name.toLowerCase().includes(location.toLowerCase())
          );
          if (matchedLoc) {
            setLocationInfo(matchedLoc);
          }
        } catch (e) {
          console.log("Location info not found");
        }
      } catch (error) {
        console.error("Error fetching search results:", error);
        setHomestays([]);
      } finally {
        setLoading(false);
      }
    };
    
    if (location) {
      fetchResults();
    }
  }, [location]);
  
  // SEO metadata
  const pageTitle = `Homestays in ${formattedLocation} | Best ${formattedLocation} Homestays`;
  const pageDescription = `Discover ${homestays.length}+ authentic homestays in ${formattedLocation}, Konkan. Book verified homestays with local hosts, home-cooked Malvani food, and scenic views. Best prices guaranteed.`;
  const canonicalUrl = `${window.location.origin}/search/${query}`;
  
  // Schema.org structured data for search results
  const searchResultsSchema = {
    "@context": "https://schema.org",
    "@type": "SearchResultsPage",
    "name": pageTitle,
    "description": pageDescription,
    "url": canonicalUrl,
    "mainEntity": {
      "@type": "ItemList",
      "itemListElement": homestays.slice(0, 10).map((homestay, index) => ({
        "@type": "ListItem",
        "position": index + 1,
        "item": {
          "@type": "LodgingBusiness",
          "name": homestay.name,
          "description": homestay.description?.substring(0, 200),
          "address": homestay.address,
          "priceRange": homestay.price_per_night ? `₹${homestay.price_per_night}` : "Contact for price",
          "url": `${window.location.origin}/homestay/${homestay.slug}`
        }
      }))
    }
  };
  
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": window.location.origin
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Search",
        "item": `${window.location.origin}/listings`
      },
      {
        "@type": "ListItem",
        "position": 3,
        "name": `Homestays in ${formattedLocation}`,
        "item": canonicalUrl
      }
    ]
  };

  // SEO: Update meta tags directly in DOM
  useEffect(() => {
    document.title = pageTitle;
    
    const updateMeta = (selector, content, isProperty = false) => {
      let meta = document.querySelector(selector);
      if (meta) {
        meta.content = content;
      } else {
        meta = document.createElement('meta');
        if (isProperty) {
          meta.setAttribute('property', selector.match(/property="([^"]+)"/)?.[1] || '');
        } else {
          meta.name = selector.match(/name="([^"]+)"/)?.[1] || '';
        }
        meta.content = content;
        document.head.appendChild(meta);
      }
    };
    
    updateMeta('meta[name="description"]', pageDescription);
    updateMeta('meta[name="robots"]', 'index, follow, max-snippet:-1, max-image-preview:large');
    
    let canonicalLink = document.querySelector('link[rel="canonical"]');
    if (!canonicalLink) {
      canonicalLink = document.createElement('link');
      canonicalLink.rel = 'canonical';
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.href = canonicalUrl;
    
    updateMeta('meta[property="og:type"]', 'website', true);
    updateMeta('meta[property="og:url"]', canonicalUrl, true);
    updateMeta('meta[property="og:title"]', pageTitle, true);
    updateMeta('meta[property="og:description"]', pageDescription, true);
    updateMeta('meta[property="og:site_name"]', 'Kokan Homestays', true);
    
    updateMeta('meta[name="twitter:card"]', 'summary_large_image');
    updateMeta('meta[name="twitter:title"]', pageTitle);
    updateMeta('meta[name="twitter:description"]', pageDescription);
    
    // Inject structured data
    const removeExistingSchemas = () => {
      document.querySelectorAll('script[type="application/ld+json"][data-search-schema="true"]').forEach(el => el.remove());
    };
    removeExistingSchemas();
    
    const addSchema = (data) => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.setAttribute('data-search-schema', 'true');
      script.textContent = JSON.stringify(data);
      document.head.appendChild(script);
    };
    
    addSchema(searchResultsSchema);
    addSchema(breadcrumbSchema);
    
  }, [pageTitle, pageDescription, canonicalUrl, searchResultsSchema, breadcrumbSchema]);

  return (
    <div className="min-h-screen bg-[#FDFBF7]" data-testid="search-results-page">
      <Navbar />
      
      {/* Hero Section */}
      <section className="pt-24 pb-12 px-6 md:px-12 bg-gradient-to-b from-primary/5 to-transparent">
        <div className="max-w-7xl mx-auto">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 text-sm text-muted-foreground mb-6">
            <Link to="/" className="hover:text-primary">Home</Link>
            <span>/</span>
            <Link to="/listings" className="hover:text-primary">Listings</Link>
            <span>/</span>
            <span className="text-foreground">{formattedLocation}</span>
          </nav>
          
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <div>
              <h1 
                className="text-4xl md:text-5xl font-semibold text-foreground mb-4"
               
              >
                Homestays in {formattedLocation}
              </h1>
              <p className="text-lg text-muted-foreground max-w-2xl">
                {locationInfo?.description || 
                  `Discover authentic homestay experiences in ${formattedLocation}. Stay with local hosts and experience the true Konkan hospitality.`}
              </p>
            </div>
            
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-full shadow-sm">
                <Home className="w-5 h-5 text-primary" />
                <span className="font-medium">{homestays.length} Homestays</span>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Results Section */}
      <section className="py-12 px-6 md:px-12">
        <div className="max-w-7xl mx-auto">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : homestays.length > 0 ? (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {homestays.map((homestay) => (
                  <HomestayCard key={homestay.id} homestay={homestay} />
                ))}
              </div>
              
              {/* SEO Content Section */}
              <div className="mt-16 bg-white rounded-2xl p-8 shadow-soft">
                <h2 
                  className="text-2xl font-semibold text-foreground mb-4"
                 
                >
                  About Homestays in {formattedLocation}
                </h2>
                <div className="prose prose-lg text-muted-foreground max-w-none">
                  <p>
                    {formattedLocation} is one of the most beautiful destinations in the Konkan region of Maharashtra. 
                    Our curated collection of homestays in {formattedLocation} offers authentic local experiences 
                    with warm hospitality, delicious Malvani cuisine, and stunning natural beauty.
                  </p>
                  <p>
                    Whether you're looking for a peaceful beach retreat, a hillside escape, or a traditional 
                    village experience, our {formattedLocation} homestays provide the perfect base to explore 
                    the region's temples, beaches, forts, and local markets.
                  </p>
                  <h3>Why Choose a Homestay in {formattedLocation}?</h3>
                  <ul>
                    <li>Authentic Konkani hospitality with local families</li>
                    <li>Home-cooked Malvani meals with fresh seafood</li>
                    <li>Personalized travel tips and local insights</li>
                    <li>Better value compared to hotels</li>
                    <li>Unique cultural immersion experience</li>
                  </ul>
                </div>
              </div>
            </>
          ) : (
            <div className="text-center py-20">
              <MapPin className="w-16 h-16 text-muted-foreground/30 mx-auto mb-4" />
              <h2 className="text-2xl font-semibold text-foreground mb-2">
                No homestays found in {formattedLocation}
              </h2>
              <p className="text-muted-foreground mb-6">
                We couldn't find any homestays matching your search. Try a different location or browse all listings.
              </p>
              <Link to="/listings">
                <Button className="bg-primary text-white hover:bg-primary/90 rounded-full px-8">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Browse All Homestays
                </Button>
              </Link>
            </div>
          )}
        </div>
      </section>
      
      {/* Related Searches - SEO Internal Links */}
      <section className="py-12 px-6 md:px-12 bg-secondary/30">
        <div className="max-w-7xl mx-auto">
          <h2 
            className="text-2xl font-semibold text-foreground mb-6"
           
          >
            Explore More Destinations
          </h2>
          <div className="flex flex-wrap gap-3">
            {["Dapoli", "Ratnagiri", "Ganpatipule", "Tarkarli", "Alibaug", "Murud", "Harihareshwar", "Guhagar", "Malvan"].map((loc) => (
              loc.toLowerCase() !== location.toLowerCase() && (
                <Link
                  key={loc}
                  to={`/search/homestays-in-${loc.toLowerCase()}`}
                  className="px-4 py-2 bg-white rounded-full text-sm font-medium text-foreground hover:bg-primary hover:text-white transition-colors shadow-sm"
                >
                  Homestays in {loc}
                </Link>
              )
            ))}
          </div>
        </div>
      </section>
      
      <Footer />
    </div>
  );
};

export default SearchResultsPage;
